import React, { useState, useEffect } from 'react';
import { styled, Theme } from '@material-ui/core/styles';

import MenuBar from './components/MenuBar/MenuBar';
import MobileTopMenuBar from './components/MobileTopMenuBar/MobileTopMenuBar';
import PreJoinScreens from './components/PreJoinScreens/PreJoinScreens';
import ReconnectingNotification from './components/ReconnectingNotification/ReconnectingNotification';
import RecordingNotifications from './components/RecordingNotifications/RecordingNotifications';
import ConsentNotification from './components/ConsentNotification/ConsentNotification';
import FollowUpNotification from './components/FollowUpNotification/FollowUpNotification';
import Room from './components/Room/Room';

import useHeight from './hooks/useHeight/useHeight';
import useRoomState from './hooks/useRoomState/useRoomState';
import * as Sentry from '@sentry/react';
import MaintenancePage from './components/MaintenancePage/MaintenancePage';
import getFeatureFlag from './appConfig';

const Container = styled('div')({
  display: 'grid',
  gridTemplateRows: '1fr auto',
});

const Main = styled('main')(({ theme }: { theme: Theme }) => ({
  overflow: 'scroll',
  paddingBottom: `${theme.footerHeight}px`, // Leave some space for the footer
  background: 'black',
  [theme.breakpoints.down('sm')]: {
    paddingBottom: `${theme.mobileFooterHeight + theme.mobileTopBarHeight}px`, // Leave some space for the mobile header and footer
  },
}));

export default function App() {
  const roomState = useRoomState();
  const [maintenanceModeEnabled, setMaintenanceModeEnabled] = useState({
    flag: 'main',
    maintenanceEnabled: false,
    otpEnabled: false,
  });
  // Here we would like the height of the main container to be the height of the viewport.
  // On some mobile browsers, 'height: 100vh' sets the height equal to that of the screen,
  // not the viewport. This looks bad when the mobile browsers location bar is open.
  // We will dynamically set the height with 'window.innerHeight', which means that this
  // will look good on mobile browsers even after the location bar opens or closes.
  const height = useHeight();
  useEffect(() => {
    let isMounted = true;
    async function fetchFeatureFlag() {
      const featureFlagValue = await getFeatureFlag();
      if (isMounted) setMaintenanceModeEnabled(featureFlagValue);
    }
    fetchFeatureFlag();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const checkTabActive = () => {
      Sentry.addBreadcrumb({
        type: 'debug',
        category: '###tabVisibility',
        level: Sentry.Severity.Info,
        message: document.visibilityState,
      });
    };
    window.addEventListener('visibilitychange', checkTabActive);
    return () => {
      window.removeEventListener('visibilitychange', checkTabActive);
    };
  }, []);

  useEffect(() => {
    const windowOnBlur = () => {
      Sentry.addBreadcrumb({
        type: 'debug',
        category: '###windowOnBlur',
        level: Sentry.Severity.Info,
        message: 'onBlur',
      });
    };
    window.addEventListener('blur', windowOnBlur);
    return () => {
      window.removeEventListener('blur', windowOnBlur);
    };
  }, []);
  useEffect(() => {
    const windowOnFocus = () => {
      Sentry.addBreadcrumb({
        type: 'debug',
        category: '###windowOnFocus',
        level: Sentry.Severity.Info,
        message: 'onFocus',
      });
    };
    window.addEventListener('focus', windowOnFocus);
    return () => {
      window.removeEventListener('focus', windowOnFocus);
    };
  }, []);

  return (
    <Container style={{ height }}>
      {maintenanceModeEnabled.maintenanceEnabled ? (
        <MaintenancePage flag={maintenanceModeEnabled.flag} />
      ) : (
        <>
          {roomState === 'disconnected' ? (
            <PreJoinScreens otpEnabled={maintenanceModeEnabled.otpEnabled} />
          ) : (
            <Main>
              <ReconnectingNotification />
              <RecordingNotifications />
              <ConsentNotification />
              <FollowUpNotification />
              <MobileTopMenuBar />
              <Room />
              <MenuBar />
            </Main>
          )}
        </>
      )}
    </Container>
  );
}
